import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {StandaloneCheckbox} from "../../../../base/checkbox/checkbox";
import {issueCreditFromInvoiceItemSelectionDataGridColumnNames} from "../../../../../../core/constants/table/data-grid-column-keys";
import {DataGridColumnAlignments, DataGridColumnPinnedTypes, DataGridColumnTypes, useDataGridSavedState} from "../../../../../../packages/data-grid";
import {Box, DialogActions, DialogContent, Divider, MenuItem, TextField, Typography} from "@mui/material";
import ProductInformationPopover from "../../../../app-specific/draggable-popover/product-information-popover";
import IconButton from "../../../../base/button/icon_button";
import {Add, Remove} from "@mui/icons-material";
import PartnerDataGrid from "../../../../../containers/data-grid";
import Button from "../../../../base/button/button";
import Api from "../../../../../../core/services/api/api";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import {deepCopy} from "../../../../../../core/services/utils/utils";
import {ProductTypes} from "../../../../../../core/constants/enums";
import useAccessLevels from "../../../../../hooks/use-access-levels";


const dataGridSaveConfig = {
    version: 2,
    name: 'issueCreditFromInvoiceItemSelectionDataGrid',
}


const SelectItemsSection = ({items, setItems, selectedItems, onNextStep, onPreviousStep, warehouses}) => {
    const operatorAccessLevels = useAccessLevels();
    const [productAvailabilities, setProductAvailabilities] = useState({});
    const [creatingItemInSelectedWarehouse, setCreatingItemInSelectedWarehouse] = useState([]);
    const [selectionState, setSelectionState] = useState(selectedItems.reduce((acc, selectedItem) => {
        const isSerialized = selectedItem.isSerialized;
        acc.push({
            itemId: selectedItem.id,
            serials: isSerialized ? selectedItem.serials : [],
            isItemSerialized: isSerialized,
        })
        return acc;
    }, []));
    const isMounted = useIsMounted();

    const productAvailabilitiesRef = useRef(productAvailabilities);
    productAvailabilitiesRef.current = productAvailabilities;

    const allChecked = useMemo(() => {
        // if all item ids are in the list of selection state and for each serialized item, all serials are selected
        return items.length > 0
            && items.every(item => {
                const selection = selectionState.find(e => e.itemId === item.id);
                if (!selection)
                    return false;
                if (item.isSerialized)
                    return item.serials.length === selection.serials.length;
                return true;
            })
    }, [selectionState, items]);
    const canNotProceed = useMemo(() => {
        if (selectionState.length === 0)
            return true;
        if (creatingItemInSelectedWarehouse.length)
            return true;
        // the selected warehouses of selected items must not be isComingSoon
        return items.some(item => {
            const selection = selectionState.find(e => e.itemId === item.id);
            if (!selection)
                return false;
            const availability = productAvailabilities[item.partNo]?.availability?.find(e => e.warehouseCode === item.warehouseCode);
            return availability?.isComingSoon;
        })
    }, [creatingItemInSelectedWarehouse.length, items, productAvailabilities, selectionState])

    /**
     * Fetches the available quantities of order products from the api.
     * - sets the items' availability in the state.
     * @type {(partNos: any) => Promise<void>}
     */
    const getProductsAvailabilities = useCallback(async (partNos) => {
        if (!partNos || !partNos.length)
            return;

        setProductAvailabilities(prevState => {
            for (const partNo of partNos) {
                prevState[partNo] = {
                    ...(prevState[partNo] ?? {}),
                    loading: true,
                }
            }
            return {...prevState};
        })
        const response = await Api.getProductAvailabilitiesV2({
            partNumbers: partNos,
        });
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setProductAvailabilities(prevState => {
                for (const partNo of partNos) {
                    const data = response.data?.find(e => e.partNumber === partNo);
                    prevState[partNo] = {
                        ...(prevState[partNo] ?? {}),
                        availability: data?.localWarehouses ?? [],
                    }
                }
                return {...prevState};
            })
        }
        setProductAvailabilities(prevState => {
            for (const partNo of partNos) {
                prevState[partNo] = {
                    ...(prevState[partNo] ?? {}),
                    loading: false,
                }
            }
            return {...prevState};
        })
    }, [isMounted])

    /**
     * With each change in the [items] list:
     * - determines which one of the availabilities must be removed and which ones must be added.
     */
    useEffect(() => {
        const partNos = Array.from(new Set(items?.map(e => e.partNo) ?? []));
        const _productAvailabilities = deepCopy(productAvailabilitiesRef.current);

        // remove the availabilities of removed products
        for (const partNo of Object.keys(_productAvailabilities ?? {})) {
            if (!partNos.includes(partNo)) {
                // must be removed
                delete _productAvailabilities[partNo];
            }
        }

        const newPartNos = partNos.filter(partNo => _productAvailabilities[partNo] === undefined);
        if (newPartNos.length) {
            getProductsAvailabilities(newPartNos).then();
        }
    }, [getProductsAvailabilities, items])
    /**
     * Sets all items to checked or unchecked for the selection.
     * @type {(function(*): void)}
     */
    const setAllChecked = useCallback((checked) => {
        setSelectionState(() => {
            if (checked) {
                return items.map(item => ({
                    itemId: item.id,
                    isItemSerialized: item.isSerialized,
                    serials: item.isSerialized ? item.serials : [],
                }))
            } else {
                return [];
            }
        })
    }, [items])


    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [

            {
                title: () => {
                    return <>
                        <StandaloneCheckbox
                            checked={allChecked}
                            indeterminate={!allChecked && selectionState.length > 0}
                            onClick={() => setAllChecked(!allChecked)}
                        />
                    </>
                },
                name: issueCreditFromInvoiceItemSelectionDataGridColumnNames.selection,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.element,
                width: 34,
                pinned: true,
                sortable: false,
                resizable: false,
                reorderable: false,
                visibilityToggleable: false,
                pinnedToggleable: false,
            },
            {
                name: issueCreditFromInvoiceItemSelectionDataGridColumnNames.groupBy,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                pinnedToggleable: false,
                resizable: false,
                visibilityToggleable: false,
                visible: false,
                reorderable: false,
                width: 0,
            },
            {
                title: "Part No",
                name: issueCreditFromInvoiceItemSelectionDataGridColumnNames.partNo,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                pinned: true,
                width: 130,
            },
            {
                title: "Warehouse",
                name: issueCreditFromInvoiceItemSelectionDataGridColumnNames.warehouse,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                width: 150,
            },
            {
                title: "Quantity",
                name: issueCreditFromInvoiceItemSelectionDataGridColumnNames.quantity,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.element,
                sortable: false,
                width: 150,
            },
            {
                title: "Unit Price",
                name: issueCreditFromInvoiceItemSelectionDataGridColumnNames.unitPrice,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                sortable: false,
            },
            {
                title: "Total Price",
                name: issueCreditFromInvoiceItemSelectionDataGridColumnNames.totalPrice,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
                sortable: false,
            },
            {
                title: "Committed",
                name: issueCreditFromInvoiceItemSelectionDataGridColumnNames.serialNoOrQuantity,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                sortable: false,
                pinned: true,
                pinnedType: DataGridColumnPinnedTypes.right,
                width: 250,
            },
        ], [allChecked, selectionState.length, setAllChecked])

    const [dataGridColumns, , , dataGridDensity] = useDataGridSavedState({
        columns: columns,
        version: dataGridSaveConfig.version,
        name: dataGridSaveConfig.name,
    });

    /** Handles changes in the value of the quantity text field
     * @type {(function({item: *, newValue: string, isIncrease: boolean, isDecrease: boolean}): void)}
     */
    const onSelectedQtyValueChanged = useCallback(({item, newValue, isIncrease, isDecrease}) => {
        const min = 0;
        const max = item.quantity;
        if (isIncrease) {
            setItems(prevState => prevState.map(e => e.id === item.id ? {...e, committedQuantity: Math.min(e.committedQuantity + 1, max)} : e))
        } else if (isDecrease) {
            setItems(prevState => prevState.map(e => e.id === item.id ? {...e, committedQuantity: Math.min(Math.max(e.committedQuantity - 1, 0), max)} : e))
        } else {
            let _value = parseFloat(newValue);
            if (isNaN(_value)) {
                setItems(prevState => prevState.map(e => e.id === item.id ? {...e, committedQuantity: 0} : e))
                return;
            }
            if (!operatorAccessLevels.packages.sales.allowDecimalQuantityInOrders) {
                _value = Math.trunc(_value);
            }
            if (_value < min) {
                _value = min;
            }
            if (_value > max) {
                _value = max;
            }
            setItems(prevState => prevState.map(e => e.id === item.id ? {...e, committedQuantity: _value} : e))
        }
    }, [operatorAccessLevels.packages.sales.allowDecimalQuantityInOrders, setItems])

    /**
     * Sets the quantity to 0 if the entered value is empty
     * @type {(function(*=, *): void)}
     */
    const handleInvalidQty = useCallback((event, item) => {
        if (isNaN(parseFloat(event?.target?.value))) {
            setItems(prevState => prevState.map(e => e.id === item.id ? {...e, committedQuantity: 0} : e))
        }
    }, [setItems])

    /**
     * Sets the warehouse code for the item
     * @type {(function(*, *): void)}
     */
    const setWarehouse = useCallback((event, item) => {
        setItems(prevState => prevState.map(e => e.id === item.id ? {...e, warehouseCode: event.target.value} : e))
    }, [setItems])

    /**
     * Creates an item in the selected warehouse for the provided item.
     * @type {(function(*, *): Promise<void>)}
     */
    const createItemInSelectedWarehouse = useCallback(async (item, availability) => {
        setCreatingItemInSelectedWarehouse(prevState => [...prevState, item.partNo]);
        const partInfoResponse = await Api.getPartInfoWithPartNumber({
            partNumbers: [item.partNo],
        });
        if (!isMounted())
            return;
        if (!partInfoResponse?.resultFlag) {
            setCreatingItemInSelectedWarehouse(prevState => prevState.filter(e => e !== item.partNo));
            return;
        }
        const forApi = {
            partId: partInfoResponse.data?.find(e => e.partNo === item.partNo)?.id,
            warehouseId: availability.warehouseId,
        }
        const response = await Api.createPartInWarehouse(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            await getProductsAvailabilities([item.partNo]);
        }
        if (!isMounted())
            return;
        setCreatingItemInSelectedWarehouse(prevState => prevState.filter(e => e !== item.partNo));
    }, [getProductsAvailabilities, isMounted])


    /**
     * Handles the selection change of the checkbox
     * @type {(function(*, *))}
     */
    const onSelectionChanged = useCallback((checked, item) => {
        const _setSelectionState = (stateOrCallback) => {
            setSelectionState(prevState => {
                let newState = typeof stateOrCallback === 'function' ? stateOrCallback(prevState) : stateOrCallback;
                newState = newState.filter(e => !e.isItemSerialized || e.serials.length > 0);
                return newState;
            })
        }
        const isSerialNumberRow = item.isSerialized && item.isSerialNumber;
        const isSerializedRow = item.isSerialized && !item.isSerialNumber;
        const isUnSerializedRow = !item.isSerialized;
        if (isUnSerializedRow) {
            _setSelectionState(prevState => {
                if (checked) {
                    return [...prevState, {itemId: item.id, serials: [], isItemSerialized: item.isSerialized}]
                } else {
                    return prevState.filter(e => e.itemId !== item.id);
                }
            })
        } else if (isSerializedRow) {
            _setSelectionState(prevState => {
                if (checked) {
                    const itemFound = prevState.find(e => e.itemId === item.id);
                    if (itemFound) {
                        const allSerialsSelected = item.serials.every(serial => itemFound.serials.includes(serial));
                        if (!allSerialsSelected) {
                            return prevState.map(e => e.itemId === item.id ? {...e, serials: item.serials} : e)
                        } else {
                            return prevState.filter(e => e.itemId !== item.id);
                        }
                    } else {
                        return [...prevState, {itemId: item.id, serials: item.serials, isItemSerialized: item.isSerialized}]
                    }
                } else {
                    return prevState.filter(e => e.itemId !== item.id);
                }
            })
        } else if (isSerialNumberRow) {
            _setSelectionState(prevState => {
                const itemFound = prevState.find(e => e.itemId === item.originalId);
                if (checked) {
                    if (!itemFound) {
                        return [...prevState, {itemId: item.originalId, serials: [item.serialNumber], isItemSerialized: item.isSerialized}]
                    } else {
                        return prevState.map(e => e.itemId === item.originalId
                            ? {...e, serials: [...e.serials, item.serialNumber]}
                            : e
                        )
                    }
                } else {
                    if (itemFound) {
                        const newState = prevState.map(e => e.itemId === item.originalId
                            ? {...e, serials: e.serials.filter(serial => serial !== item.serialNumber)}
                            : e
                        )
                        if (newState.find(e => e.itemId === item.originalId)?.serials.length === 0) {
                            return prevState.filter(e => e.itemId !== item.originalId);
                        }
                        return newState;
                    } else {
                        return prevState;
                    }
                }
            })
        }
    }, [])


    /**
     * Handles the change of the unit price
     * @type {(function(*, *): void)}
     */
    const handleInvalidUnitPrice = useCallback((event, item) => {
        if (!event?.target?.value?.trim()?.length || isNaN(parseFloat(event?.target?.value))) {
            setItems(prevState => prevState.map(e => e.id === item.id ? {...e, unitPrice: 0} : e))
        }
    }, [setItems])

    /**
     * Handles the change of the unit price
     * @type {(function(*, *): void)}
     */
    const onUnitPriceChanged = useCallback((e, item) => {
        const rawValue = e.target?.value ?? "";
        const value = parseFloat(rawValue);
        setItems(prevState => prevState.map(e => {
            if (e.id === item.id) {
                if (isNaN(value) || !rawValue?.trim()) {
                    return {...e, unitPrice: 0}
                } else {
                    return {...e, unitPrice: value}
                }
            }
            return e;
        }));
    }, [setItems])

    /**
     * Proceeds to the next step
     * @type {(function(): void)}
     */
    const proceedToNextStep = useCallback(() => {
        const selectedItems = items
            .filter(e => !!selectionState.find(s => s.itemId === e.id))
            .map(item => {
                const isSerialized = item.isSerialized;
                return ({
                    ...item,
                    committedQuantity: isSerialized ? selectionState.find(e => e.itemId === item.id)?.serials.length : item.committedQuantity,
                    serials: isSerialized ? selectionState.find(e => e.itemId === item.id)?.serials : [],
                })
            })
            .filter(e => e.committedQuantity > 0);
        onNextStep(selectedItems);
    }, [items, onNextStep, selectionState])

    const flattenedLength = useMemo(() => items?.reduce((acc, item) => {
        acc += item.isSerialized ? item.serials.length + 1 : 1;
        return acc;
    }, []) ?? 0, [items])

    /**@type {DataGridRow[]}*/
    const dataGridRows = useMemo(() => {
        const flattened = items.reduce((acc, item) => {
            acc.push(item);
            if (item.isSerialized) {
                for (const serial of item.serials) {
                    acc.push({
                        ...item,
                        id: `${item.id}_${serial}`,
                        quantity: 1,
                        committedQuantity: 1,
                        serialNumber: serial,
                        isSerialNumber: true,
                        originalId: item.id,
                    })
                }
            }
            return acc;
        }, [])
        return flattened.map(item => {
            const isSerializedRow = item.isSerialized && !item.isSerialNumber;
            const isUnSerializedRow = !item.isSerialized;
            const isSerialNumberRow = item.isSerialized && item.isSerialNumber;

            const selectionItem = selectionState.find(e => e.itemId === (isSerialNumberRow ? item.originalId : item.id));

            let selectionIndeterminate = false;
            let selectionChecked = false;

            if (isUnSerializedRow) {
                selectionIndeterminate = false;
                selectionChecked = !!selectionItem;
            } else if (isSerializedRow) {
                selectionIndeterminate = !!selectionItem && selectionItem.serials.length < item.serials.length;
                selectionChecked = !!selectionItem && selectionItem.serials.length === item.serials.length;
            } else if (isSerialNumberRow) {
                selectionIndeterminate = false;
                selectionChecked = !!selectionItem && selectionItem.serials.includes(item.serialNumber);
            }

            const availability = productAvailabilities[item.partNo]?.availability?.find(e => e.warehouseCode === item.warehouseCode);
            return ({
                key: item.id,
                data: item,
                cells: {
                    [issueCreditFromInvoiceItemSelectionDataGridColumnNames.selection]:
                        <StandaloneCheckbox
                            checked={selectionChecked}
                            indeterminate={selectionIndeterminate}
                            onChange={(e, checked) => onSelectionChanged(checked, item)}
                        />,
                    [issueCreditFromInvoiceItemSelectionDataGridColumnNames.groupBy]: {
                        groupValue: `${item.partNo}_WAREHOUSE_${item.warehouseCode}`,
                    },
                    [issueCreditFromInvoiceItemSelectionDataGridColumnNames.warehouse]:
                        (isUnSerializedRow || isSerializedRow)
                            ? <TextField
                                size={'small'}
                                select
                                value={item.warehouseCode}
                                className={'mr-2'}
                                onChange={(e) => setWarehouse(e, item)}
                            >
                                {
                                    warehouses.map(warehouse => (
                                        <MenuItem key={warehouse.code} value={warehouse.code}>
                                            {warehouse.name} ({warehouse.code})
                                        </MenuItem>
                                    ))
                                }
                            </TextField>
                            : ""
                    ,
                    [issueCreditFromInvoiceItemSelectionDataGridColumnNames.partNo]:
                        (isUnSerializedRow || isSerializedRow)
                            ? <ProductInformationPopover partNo={item.partNo}>
                                {item.partNo ?? ""}
                            </ProductInformationPopover>
                            : '',
                    [issueCreditFromInvoiceItemSelectionDataGridColumnNames.quantity]:
                        (isUnSerializedRow || isSerializedRow)
                            ? availability?.isComingSoon
                                ? <Button
                                    onClick={() => createItemInSelectedWarehouse(item, availability)}
                                    disabled={creatingItemInSelectedWarehouse.includes(item.partNo)}
                                    variant={'outlined'}
                                    color={'primary'}
                                >
                                    {
                                        creatingItemInSelectedWarehouse.includes(item.partNo)
                                            ? "Creating in warehouse..."
                                            : "Create in warehouse"
                                    }
                                </Button>
                                : item.quantity
                            : '',
                    [issueCreditFromInvoiceItemSelectionDataGridColumnNames.unitPrice]:
                        (isUnSerializedRow || isSerializedRow)
                            ? <TextField
                                size={'small'}
                                fullWidth
                                inputProps={{min: 0}}
                                type={'number'}
                                placeholder={''}
                                className={'mr-2'}
                                value={item.unitPrice?.toString()}
                                onBlur={(e) => handleInvalidUnitPrice(e, item)}
                                onChange={(e) => onUnitPriceChanged(e, item)}
                                InputProps={{
                                    startAdornment: '$',
                                }}
                            />
                            : ''
                    ,
                    [issueCreditFromInvoiceItemSelectionDataGridColumnNames.totalPrice]:
                        (isUnSerializedRow || isSerializedRow)
                            ? Number((item.unitPrice ?? '0') * (item.quantity ?? 0))
                            : '',
                    [issueCreditFromInvoiceItemSelectionDataGridColumnNames.serialNoOrQuantity]:
                        <>
                            {
                                isUnSerializedRow && (
                                    !item?.typeInfo?.code || item?.typeInfo?.code === ProductTypes.nonPhysical
                                        ? <Typography variant={'body2'}>
                                            {item?.typeInfo?.code === ProductTypes.nonPhysical ? 'Non-physical' : 'N/A (Non-physical)'}
                                        </Typography>
                                        : <Box className={'d-flex align-items-center justify-content-center'}>
                                            <IconButton
                                                onPressed={() => onSelectedQtyValueChanged({isDecrease: true, item: item})}
                                                disabled={item.committedQuantity <= 0}
                                                color={'secondary'}
                                            >
                                                <Remove className={'icon small'}/>
                                            </IconButton>
                                            <div className={'quantity-field mx-2'}>
                                                <TextField
                                                    size={'small'}
                                                    fullWidth
                                                    inputProps={{min: 0}}
                                                    type={'number'}
                                                    placeholder={''}
                                                    sx={{
                                                        '& input': {
                                                            textAlign: 'center',
                                                        }
                                                    }}
                                                    value={item.committedQuantity?.toString()}
                                                    onBlur={(e) => handleInvalidQty(e, item)}
                                                    onChange={(e) => onSelectedQtyValueChanged({newValue: e.target.value, item: item})}
                                                />
                                            </div>
                                            <IconButton
                                                onPressed={() => onSelectedQtyValueChanged({isIncrease: true, item: item})}
                                                disabled={item.committedQuantity >= item.quantity}
                                                color={'secondary'}
                                            >
                                                <Add className={'icon small'}/>
                                            </IconButton>
                                        </Box>)
                            }
                            {
                                isSerializedRow &&
                                <>
                                    <Typography variant={'caption'} color={'text.secondary'} fontWeight={600}>
                                        Serials: {item.serials.slice(0, 5).join(', ').concat(item.serials.length > 5 ? '...' : '')}
                                    </Typography>
                                </>
                            }
                            {
                                isSerialNumberRow &&
                                <>
                                    <Typography variant={'body2'}>
                                        {item.serialNumber ?? ""}
                                    </Typography>
                                </>
                            }
                        </>
                },
            })
        })
    }, [createItemInSelectedWarehouse, creatingItemInSelectedWarehouse, handleInvalidQty, handleInvalidUnitPrice, items, onSelectedQtyValueChanged, onSelectionChanged, onUnitPriceChanged, productAvailabilities, selectionState, setWarehouse, warehouses])

    return (
        <>
            <DialogContent className={'mt--4'}>
                <div className={'w-100 px-3 pb-4 d-flex flex-column'}>
                    <Typography variant={'body2'} className={'mb-3'}>
                        Please select the items you would like to issue credit for.
                    </Typography>
                    <PartnerDataGrid
                        {...dataGridSaveConfig}
                        columns={dataGridColumns}
                        hideColumnFiltering
                        initialLayoutState={{columns: columns}}
                        hideFooter
                        classNames={{
                            container: "px-0",
                            toolbar: 'pt-0',
                        }}
                        hideExporting
                        visibleRows={5}
                        rows={dataGridRows}
                        state={{
                            density: dataGridDensity,
                            pagination: {currentPage: 1, pageSize: flattenedLength},
                            group: {
                                columnName: issueCreditFromInvoiceItemSelectionDataGridColumnNames.groupBy,
                                showOnColumn: issueCreditFromInvoiceItemSelectionDataGridColumnNames.partNo,
                            },
                        }}
                    />
                </div>
            </DialogContent>
            <DialogActions className={'p-0 d-flex flex-column'}>
                <Divider flexItem variant={'fullWidth'}/>
                <Box sx={{marginLeft: '0 !important', display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%'}}>
                    <Button
                        variant={'dialog'}
                        color={'primary'}
                        onClick={() => onPreviousStep()}
                        borderRadius={'0 8px 0 8px'}
                    >
                        Back
                    </Button>
                    <Button
                        variant={'dialog'}
                        color={'primary'}
                        borderRadius={'8px 0 8px 0'}
                        disabled={canNotProceed}
                        onClick={proceedToNextStep}
                    >
                        Proceed
                    </Button>
                </Box>
            </DialogActions>
        </>
    );
}

export default SelectItemsSection;